import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { mediaName, reviewName } from '@/lib/labels';
import type {
  AssistantStatus, ChatContext, ChatStep, ConversationDetail, DailyDigest, MonitorIncident, MonitorStatus,
  NotificationSummary, Severity, WatchLevel, WatchList,
} from '@/types/assistant';

export const LEVEL_LABEL: Record<WatchLevel, string> = { all: '全部', important: '重要', off: '關閉' };
export const LEVEL_HINT: Record<WatchLevel, string> = {
  all: '異常、恢復與更新結果都通知',
  important: '只通知警告與嚴重的異常',
  off: '這個範圍不通知',
};
export const SEVERITY_LABEL: Record<Severity, string> = { info: '資訊', warning: '警告', critical: '嚴重' };

/* /auth/me 不回 id，快取鍵用帳號名稱，換帳號時才不會沿用前一位的資料。 */
function useAccount() {
  const { user } = useAuth();
  return user?.username ?? '';
}

export function useNotificationSummary() {
  const account = useAccount();
  return useQuery({
    queryKey: ['notifications', 'summary', account],
    queryFn: () => apiGet<NotificationSummary>('/api/v1/notifications/summary'),
    enabled: Boolean(account),
    refetchInterval: 30_000,
  });
}

export function useWatchList() {
  const account = useAccount();
  return useQuery({
    queryKey: ['notifications', 'subscriptions', account],
    queryFn: () => apiGet<WatchList>('/api/v1/notifications/subscriptions'),
    enabled: Boolean(account),
    staleTime: 60_000,
  });
}

export function useAssistantStatus() {
  const account = useAccount();
  return useQuery({
    queryKey: ['assistant', 'status', account],
    queryFn: () => apiGet<AssistantStatus>('/api/v1/assistant/status'),
    enabled: Boolean(account),
    staleTime: 60_000,
  });
}

export function useMonitorStatus({ enabled = true }: { enabled?: boolean } = {}) {
  const account = useAccount();
  return useQuery({
    queryKey: ['monitor', 'status', account],
    queryFn: () => apiGet<MonitorStatus>('/api/v1/monitor/status'),
    enabled: Boolean(account) && enabled,
    refetchInterval: 60_000,
  });
}

/** 今天（或昨天）的值班摘要；後端依使用者看得到的場域挑對應的那一份。 */
export function useDailyDigest({ enabled = true }: { enabled?: boolean } = {}) {
  const account = useAccount();
  return useQuery({
    queryKey: ['assistant', 'digest', account],
    queryFn: () => apiGet<{ digest: DailyDigest | null }>('/api/v1/assistant/digest'),
    enabled: Boolean(account) && enabled,
    staleTime: 5 * 60_000,
  });
}

/** 還沒解除的異常（SD 卡快滿、上傳一直失敗等），後端已依使用者看得到的場域過濾。 */
export function useOpenIncidents({ enabled = true }: { enabled?: boolean } = {}) {
  const account = useAccount();
  return useQuery({
    queryKey: ['monitor', 'incidents', 'open', account],
    queryFn: () => apiGet<MonitorIncident[]>('/api/v1/monitor/incidents?status=open&limit=200'),
    enabled: Boolean(account) && enabled,
    refetchInterval: 60_000,
  });
}

/** 訂閱鈴鐺顯示的層級，規則和後端 resolve_level 相同：裝置 > 場域 > 整體預設。 */
export function effectiveLevel(
  list: WatchList | undefined, scope: 'site' | 'device', key: string, siteCode?: string | null,
) {
  const find = (type: string, k: string) =>
    list?.items?.find((s) => s.scope_type === type && s.scope_key === k)?.level ?? null;
  const own = find(scope, key);
  const site = scope === 'device' && siteCode ? find('site', siteCode) : null;
  const inherited: WatchLevel = site ?? list?.overall ?? 'important';
  return { own, inherited, inheritedFrom: site ? 'site' as const : 'all' as const, level: own ?? inherited };
}

/** 「3 分鐘前」這類相對時間；超過一週改顯示日期。 */
export function timeAgo(iso: string | null | undefined, now = Date.now()) {
  if (!iso) return '';
  const diff = Math.max(0, now - new Date(iso).getTime());
  const min = Math.floor(diff / 60_000);
  if (min < 1) return '剛剛';
  if (min < 60) return `${min} 分鐘前`;
  const hour = Math.floor(min / 60);
  if (hour < 24) return `${hour} 小時前`;
  const day = Math.floor(hour / 24);
  if (day < 7) return `${day} 天前`;
  return new Date(iso).toLocaleDateString('zh-TW');
}

/* ── 對話 ── */

export interface ChatEntry {
  key: string;
  role: 'user' | 'assistant';
  text: string;
  steps: ChatStep[];
  actionIds: number[];
  failed?: boolean;
}

/** 重新開啟面板時，把伺服器上的對話紀錄還原成畫面用的格式：工具步驟併到同一輪的回覆上。 */
export function entriesFromConversation(detail: ConversationDetail): ChatEntry[] {
  const entries: ChatEntry[] = [];
  const pending = new Map<number | null, ChatStep[]>();
  for (const m of detail.messages) {
    if (m.role === 'tool') {
      const list = pending.get(m.run_id) ?? [];
      list.push({ tool: m.tool_name ?? '', label: m.label ?? m.tool_name ?? '', ok: true, summary: m.content });
      pending.set(m.run_id, list);
    } else if (m.role === 'user') {
      entries.push({ key: `m${m.id}`, role: 'user', text: m.content, steps: [], actionIds: [] });
    } else {
      entries.push({
        key: `m${m.id}`, role: 'assistant', text: m.content, steps: pending.get(m.run_id) ?? [],
        actionIds: detail.actions.filter((a) => a.run_id === m.run_id).map((a) => a.id),
      });
    }
  }
  return entries;
}

/** 快速提問。新對話列出完整問句；對話開始後縮成輸入框上方的短標籤，隨時能再問一次。
 *  前兩題分別對應後端的 get_overview 與 list_devices。 */
export interface QuickAsk { label: string; text: string; roles?: string[] }

export const QUICK_ASKS: QuickAsk[] = [
  { label: '系統狀態', text: '現在整個系統的狀態如何？' },
  { label: '每台裝置', text: '列出每台裝置目前的狀態。' },
  { label: '需要注意', text: '目前有沒有離線或需要注意的裝置？' },
  { label: '上傳失敗', text: '最近 24 小時有哪些上傳失敗？' },
  { label: 'SD 卡空間', text: '幫我看各台裝置的 SD 卡空間。' },
  { label: '可清理檔案', text: '整理一下可以清掉的上傳失敗檔案。', roles: ['admin'] },
];

/** 單台裝置要使用者補上 ID，所以只填進輸入框，不直接送出 */
export const DEVICE_ASK_PREFIX = '幫我檢查這台裝置的狀態：';

/* ── 頁面情境 ──
 * 問「這台」「這些檔案」時，小哨要知道使用者正在看哪裡。情境只是提示：
 * 後端會重新檢查頁面、篩選值、裝置與場域是否存在且有權限，不通過的直接丟掉。 */

// 和後端 assistant_rules.PAGE_LABEL 同一份
const PAGE_NAME: Record<string, string> = {
  '/overview': '總覽', '/devices': '裝置', '/events': '事件中心', '/media': '檔案管理', '/datasets': '資料集',
  '/backup': '備份', '/ota': '韌體更新', '/sites': '場域', '/users': '帳號管理', '/assistant': '小哨與 AI',
};

const STATUS_NAME: Record<string, (status: string) => string> = {
  '/devices': (s) => (s === 'offline' ? '只看離線' : s),
  '/events': reviewName,
  '/media': (s) => (s === 'in_flight' ? '待完成傳輸' : s === 'deleted' ? '已刪除紀錄' : mediaName(s)),
};

/** 網址上的頁面與篩選條件 → 送給 /assistant/chat 的情境。參數名稱和各頁的 useSearchParams 一致。 */
export function buildChatContext(pathname: string, search: string): ChatContext {
  const path = `/${pathname.split('/')[1] ?? ''}`;
  if (!PAGE_NAME[path]) return {};
  const q = new URLSearchParams(search);
  const pick = (key: string) => q.get(key)?.trim() || undefined;
  const ctx: ChatContext = { path };
  if (path === '/devices') ctx.status = pick('status');
  if (path === '/media') Object.assign(ctx, { status: pick('status'), device_id: pick('device') });
  if (path === '/events') Object.assign(ctx, { status: pick('state'), device_id: pick('device'), site_code: pick('site') });
  return Object.fromEntries(Object.entries(ctx).filter(([, v]) => v !== undefined)) as ChatContext;
}

/** 輸入框上方「依目前畫面回答：…」的文字。只有總覽這類沒有焦點的頁面不顯示。 */
export function contextLabel(ctx: ChatContext) {
  if (ctx.incident_id) return ctx.device_id ? `裝置 ${ctx.device_id} 的異常通知` : '這則異常通知';
  const parts: string[] = [];
  if (ctx.device_id) parts.push(`裝置 ${ctx.device_id}`);
  else if (ctx.path && ctx.path !== '/overview' && ctx.path !== '/assistant') parts.push(PAGE_NAME[ctx.path] ?? '');
  if (ctx.path && ctx.status) parts.push(STATUS_NAME[ctx.path]?.(ctx.status) ?? ctx.status);
  if (ctx.site_code) parts.push(`場域 ${ctx.site_code}`);
  return parts.filter(Boolean).join('・');
}

const PAGE_ASKS: Record<string, QuickAsk[]> = {
  '/devices': [
    { label: '離線原因', text: '目前離線的裝置各離線多久了？可能是什麼原因？' },
    { label: 'SD 卡空間', text: '幫我看各台裝置的 SD 卡空間。' },
  ],
  '/events': [
    { label: '待確認事件', text: '最近 24 小時有哪些待確認的事件？' },
    { label: '事件來源', text: '最近 24 小時的事件主要來自哪幾台裝置、哪些類型？' },
  ],
  '/media': [
    { label: '上傳失敗', text: '最近 24 小時有哪些上傳失敗？主要是什麼原因？' },
    { label: '卡住的傳輸', text: '有沒有停在上傳中或驗證中很久的檔案？' },
    { label: '可清理檔案', text: '整理一下可以清掉的上傳失敗檔案。', roles: ['admin'] },
  ],
  '/ota': [
    { label: '部署進度', text: '目前的韌體更新部署進度如何？有沒有卡住的裝置？' },
    { label: '更新失敗', text: '最近的韌體更新有哪些裝置失敗？原因是什麼？' },
  ],
  '/backup': [
    { label: '備份狀況', text: '目前備份狀況如何？有多少檔案還沒備份？' },
    { label: '還原演練', text: '最近幾批備份和還原演練的結果如何？', roles: ['admin'] },
  ],
  '/datasets': [
    { label: '資料集概況', text: '目前有哪些資料集？各有多少事件，標註狀態如何？' },
    { label: '匯出狀態', text: '最近的資料集匯出有沒有失敗或快過期的？' },
  ],
  '/users': [
    { label: '最近操作', text: '查稽核紀錄：最近 7 天有哪些重要操作？', roles: ['admin'] },
    { label: '刪除紀錄', text: '查稽核紀錄：最近 7 天有哪些刪除操作？是誰做的？', roles: ['admin'] },
  ],
};

/** 依情境排出快速提問：先放這台裝置或這一頁的，再補一般的，最多 6 個。
 *  問句寫出裝置 ID，對話紀錄之後重看也知道在問哪一台。 */
export function quickAsksFor(ctx: ChatContext, role: string): QuickAsk[] {
  const own: QuickAsk[] = ctx.incident_id
    ? [{ label: '異常原因', text: '這個異常可能是什麼原因？建議怎麼處理？' }]
    : [];
  if (ctx.device_id) {
    own.push(
      { label: '這台狀態', text: `裝置 ${ctx.device_id} 現在的狀態如何？` },
      { label: '這台異常', text: `裝置 ${ctx.device_id} 最近有沒有異常？可能是什麼原因？` },
      { label: '這台上傳', text: `裝置 ${ctx.device_id} 最近 24 小時的上傳狀況如何？有沒有失敗的檔案？` },
      { label: '這台日誌', text: `幫我讀裝置 ${ctx.device_id} 最近回傳的日誌，有沒有需要注意的地方？` },
    );
  } else {
    own.push(...(PAGE_ASKS[ctx.path ?? ''] ?? []));
  }
  const seen = new Set<string>();
  return [...own, ...QUICK_ASKS]
    .filter((a) => (!a.roles || a.roles.includes(role)) && !seen.has(a.label) && Boolean(seen.add(a.label)))
    .slice(0, 6);
}

/** 其他畫面（裝置詳情、通知）要請小哨回答時，丟這個事件；小哨會切到對話分頁並直接送出。
 *  fresh 表示開新對話，不接在原本的話題後面。 */
export interface CompanionAsk { text: string; context?: ChatContext; fresh?: boolean }
export const ASK_COMPANION = 'edgeai:ask-companion';
export function askCompanion(ask: CompanionAsk) {
  window.dispatchEvent(new CustomEvent<CompanionAsk>(ASK_COMPANION, { detail: ask }));
}

/** 模型習慣用 Markdown 條列；面板只顯示純文字，把粗體與標題符號拿掉，條列改成圓點。 */
export function plainReply(text: string) {
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^(\s*)[-*]\s+/gm, '$1• ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
